import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const FlatPlan = () => {
  const navigate = useNavigate();
  const [magazines, setMagazines] = useState([]);
  const [flatPlans, setFlatPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlan, setNewPlan] = useState({
    magazine: '',
    issue: '',
    totalPages: 32,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [magazinesRes, flatPlansRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/magazines`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/flat-plans`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setMagazines(magazinesRes.data);
      setFlatPlans(flatPlansRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async e => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/flat-plans`, newPlan, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Flat plan created successfully');
      setShowCreateModal(false);
      navigate(`/flat-plan/${response.data._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create flat plan');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this flat plan?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/flat-plans/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Flat plan deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete flat plan');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Flat Plans</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Create New Flat Plan
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {flatPlans.map(plan => (
          <div key={plan._id} className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-2">{plan.magazine?.name}</h3>
            <p className="text-gray-600 mb-1">Issue: {plan.issue}</p>
            <p className="text-gray-600 mb-1">Pages: {plan.totalPages}</p>
            <p className="text-gray-600 mb-4">
              Status: <span className="capitalize">{plan.status.replace('_', ' ')}</span>
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/flat-plan/${plan._id}`)}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(plan._id)}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {flatPlans.length === 0 && (
        <div className="text-center text-gray-500 mt-8">
          No flat plans yet. Create your first one!
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">Create New Flat Plan</h2>
            <form onSubmit={handleCreatePlan}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Magazine</label>
                <select
                  value={newPlan.magazine}
                  onChange={e => setNewPlan({ ...newPlan, magazine: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="">Select Magazine</option>
                  {magazines.map(mag => (
                    <option key={mag._id} value={mag._id}>
                      {mag.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Issue (e.g., Jan25)</label>
                <input
                  type="text"
                  value={newPlan.issue}
                  onChange={e => setNewPlan({ ...newPlan, issue: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Jan25"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Total Pages</label>
                <input
                  type="number"
                  value={newPlan.totalPages}
                  onChange={e =>
                    setNewPlan({
                      ...newPlan,
                      totalPages: parseInt(e.target.value),
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                  min="1"
                  required
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlatPlan;
